import type { ReactNode } from "react";
import { Switch } from "../ui/switch";

export interface UploadScopeLabels {
    pricesLabel: string;
    pricesHint: string;
    /**
     * Replaces `pricesHint` under a price switch that is not available: why
     * there are no prices to send. Only a caller that can pass
     * `pricesAvailable={false}` needs one.
     */
    pricesUnavailable?: string;
    minLosLabel: string;
    minLosHint: string;
    /** Replaces `minLosHint` and disables the switch when restrictions are unavailable. */
    minLosUnavailable: string;
    nothingSelected: string;
}

interface UploadScopeSwitchesProps {
    prices: boolean;
    onPricesChange: (value: boolean) => void;
    /**
     * Whether there is a price to write at all. False when the source data
     * holds none: a price-list file made of rows held for their minimum stay
     * alone. A send from the plan always has prices.
     */
    pricesAvailable?: boolean;
    minLos: boolean;
    onMinLosChange: (value: boolean) => void;
    /**
     * Whether a minimum stay can be written at all — false when the provider
     * cannot receive restrictions, or when the source data has none to send.
     */
    minLosAvailable?: boolean;
    /**
     * Said under the minimum-stay switch while it is on: what sending minimum
     * stays will do on this PMS (`MinStayPerNightNotice`).
     */
    minLosNotice?: ReactNode;
    labels: UploadScopeLabels;
}

/**
 * The two switches that decide what an upload actually writes.
 *
 * Prices and minimum stay are independent layers, so this is two switches
 * rather than one three-way choice: a user can push new rates without touching
 * restrictions, change a minimum stay without moving a price, or do both.
 *
 * Labels arrive as props rather than being read from `useLanguage()` here, so
 * the same control can carry send wording on the drafts dialog and restore
 * wording in the upload log.
 */
export function UploadScopeSwitches({
    prices,
    onPricesChange,
    pricesAvailable = true,
    minLos,
    onMinLosChange,
    minLosAvailable = true,
    minLosNotice,
    labels,
}: UploadScopeSwitchesProps) {
    return (
        <div className="space-y-2">
            <div className="flex items-start justify-between gap-3 rounded-md border border-dashed border-border p-3">
                <div className="min-w-0">
                    <p className="text-sm font-medium">{labels.pricesLabel}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        {pricesAvailable ? labels.pricesHint : labels.pricesUnavailable}
                    </p>
                </div>
                <Switch
                    checked={prices && pricesAvailable}
                    onCheckedChange={onPricesChange}
                    disabled={!pricesAvailable}
                    aria-label={labels.pricesLabel}
                />
            </div>

            <div className="rounded-md border border-dashed border-border p-3">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-sm font-medium">{labels.minLosLabel}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            {minLosAvailable ? labels.minLosHint : labels.minLosUnavailable}
                        </p>
                    </div>
                    <Switch
                        checked={minLos && minLosAvailable}
                        onCheckedChange={onMinLosChange}
                        disabled={!minLosAvailable}
                        aria-label={labels.minLosLabel}
                    />
                </div>
                {minLos && minLosAvailable && minLosNotice}
            </div>

            {/* An upload with neither layer selected would be a no-op — say so
                here rather than letting the confirm button fail silently. */}
            {!prices && !minLos && (
                <p className="text-xs text-destructive">{labels.nothingSelected}</p>
            )}
        </div>
    );
}
